import type { GlyphName } from "@/landing/lib/pixel-glyphs";
import type { AgentName, Platform } from "@/landing/system/marks";

/**
 * Feature pages cut to the agent and use-case set (Grid redesign, Oct 2026).
 * Every other slug that ever existed 301s to its closest live page; the bare
 * `/<slug>` forms are handled by server/middleware/feature-redirect.ts.
 */
export const FEATURE_REDIRECTS: Record<string, string> = {
  // Closest live page.
  "claude-code-for-linkedin": "/platform/linkedin",
  "claude-code-for-reddit": "/platform/reddit",
  "claude-code-for-twitter": "/platform/x",
  "openclaw-for-tiktok-instagram": "/feature/openclaw-for-social-media",
  "ai-image-generation-cli": "/models",
  "ai-video-generation-agents": "/models",
  "image-generation": "/models",
  "meta-ads-spy-tool": "/feature/competitor-analysis-tool",
  "track-copy-competitors-automation": "/feature/competitor-analysis-tool",

  // Scraping came down to searching profiles and posts.
  "social-media-scraping-cli": "/feature/search-profiles",

  // Marketing automation and marketing agents, by audience: the one page.
  "ai-marketing-automation": "/feature/ai-agents-for-marketing",
  "ai-marketing-automation-for-agencies": "/feature/ai-agents-for-marketing",
  "ai-marketing-automation-for-creators": "/feature/ai-agents-for-marketing",
  "ai-marketing-automation-for-ecommerce": "/feature/ai-agents-for-marketing",
  "ai-marketing-automation-for-performance-marketers":
    "/feature/ai-agents-for-marketing",
  "ai-marketing-automations-for-dropshipping":
    "/feature/ai-agents-for-marketing",
  "marketing-automation-for-developers": "/feature/ai-agents-for-marketing",
  "ai-marketing-agent-cli": "/feature/ai-agents-for-marketing",
  "ai-marketing-agents-for-vibecoders": "/feature/ai-agents-for-marketing",

  // Off-message pages: home.
  "ai-ads-generator": "/",
  "ai-agents-for-social-media": "/",
  "ai-background-removal-video": "/",
  "ai-caption-generator": "/",
  "ai-content-repurposing-cli": "/",
  "ai-lip-sync-generator": "/",
  "ai-marketing-for-coaches": "/",
  "ai-marketing-for-real-estate": "/",
  "ai-marketing-for-saas-mobile-apps": "/",
  "ai-marketing-for-startups": "/",
  "ai-music-generation-cli": "/",
  "ai-text-to-speech-cli": "/",
  "ai-tiktok-slideshow-maker": "/",
  "ai-ugc-reaction-video-generator": "/",
  "ai-ugc-variations": "/",
  "ai-video-editing-cli": "/",
  "ai-voiceover-generator": "/",
  "automate-instagram-posting-cli": "/",
  "automate-tiktok-posting-cli": "/",
  "claude-code-for-instagram": "/",
  "claude-code-for-tiktok": "/",
  "claude-code-marketing-cli": "/",
  "marketing-cli": "/",
  "marketing-from-the-terminal": "/",
  "opencode-for-social-media": "/",
  "social-media-automations-api": "/",
  "social-media-content-at-scale": "/",
  "social-media-scheduling-cli": "/",
  "tiktok-on-autopilot": "/",
  "ugc-on-autopilot": "/",
  "vibe-marketing-cli": "/",
  "volume-based-marketing-ai": "/",
  wab2: "/",
};

/**
 * Live feature slugs kept out of the index (noindex,follow). Empty since the
 * Oct 2026 cut; the sitemap and the route still read it.
 */
export const FEATURE_NOINDEX_SLUGS: ReadonlySet<string> = new Set();

/**
 * Feature slugs that are permanently gone (HTTP 410). Abandoned "faceless
 * TikTok farm" positioning, off-brand for the new product. These were
 * previously 301'd to /feature/tiktok-on-autopilot, which is now itself
 * noindexed, so a 410 is the correct clean exit.
 */
export const FEATURE_410S: ReadonlySet<string> = new Set([
  "passive-income-tiktok-automation",
  "faceless-tiktok-channel-ai",
  "tiktok-farm-ai-agents",
]);

export type FeatureTask = {
  platform: Platform;
  /** The pixel glyph its tile shows. */
  glyph: GlyphName;
  /** What you'd type to the agent. */
  ask: string;
  /** The real wonda command (or MCP tool) it runs, so the ask stays true. */
  command: string;
};

export type FeaturePageCopy = {
  kind: "agent" | "use-case";
  /** Nav and footer label. */
  label: string;
  /** Agent pages: the agent whose logo the hero shows. */
  agent?: AgentName;
  /** The h1 in two parts; also the OG card title. */
  heading: [string, string];
  /** The line under the h1, sentence by sentence; also the meta and OG description. */
  sub: string[];
  /** Search and share description when it should differ from `sub`. */
  description?: string;
  metaTitle: string;
  tasks: { title: string; items: FeatureTask[] };
  faq: { question: string; answer: string }[];
};

const MCP_URL = "https://api.wondercat.ai/mcp";
const LIMITS =
  "LinkedIn actions count against daily limits; Wonda warns when you pass one, or stops if you choose.";

const FAQ_WHAT_IS = (agent: string) => ({
  question: "What is Wonda?",
  answer: `Wonda is the agentic browser for social media: a real browser on your computer, signed in to your LinkedIn, X and Reddit accounts, that ${agent} drives for you.`,
});

const FAQ_SAFE = {
  question: "Is it safe for my accounts?",
  answer: `Wonda works through a real browser on your computer, signed in as you, at a human pace. ${LIMITS} It’s undetectable, designed to keep your accounts safe and secure.`,
};

const FAQ_PLATFORMS = {
  question: "What can it do on each platform?",
  answer:
    "On LinkedIn, X and Reddit it searches, reads profiles, feeds and inboxes, messages, comments, reacts and posts. On Sales Navigator it searches leads with the filters you use (title, seniority, region, industry, company and more), builds lead lists and prepares messages and invites for you to approve.",
};

const FAQ_SALES_NAV = {
  question: "Do I need a Sales Navigator seat?",
  answer:
    "Only for the Sales Navigator commands, which use the seat on your own LinkedIn account. Everything else works with a regular account.",
};

const FAQ_AGENTS = {
  question: "Which agents does it work with?",
  answer:
    "Claude Code, Codex, Claude, ChatGPT, Cursor, OpenClaw and Hermes. Ask your agent in plain words and it uses Wonda for you.",
};

const FAQ_CONNECTOR_KEYS = (agent: string) => ({
  question: `Does ${agent} see my passwords or my Wonda key?`,
  answer: `No. You sign in to each platform yourself in the Wonda browser, and ${agent} connects to Wonda with OAuth, so your key stays inside Wonda.`,
});

export const CLI_FEATURE_COPY_MAP: Record<string, FeaturePageCopy> = {
  "claude-code-for-social-media-marketing": {
    kind: "agent",
    label: "Claude Code",
    agent: "Claude Code",
    heading: ["Claude Code", "for social media marketing"],
    sub: [
      "Claude Code already knows your project.",
      "With Wonda it also finds people, works your inboxes and reaches out on LinkedIn, Sales Navigator, X and Reddit, exactly like you would by hand.",
    ],
    metaTitle: "Claude Code for Social Media Marketing | Wonda",
    tasks: {
      title: "Lists in, leads out.",
      items: [
        {
          platform: "sales-navigator",
          glyph: "list",
          ask: "Find the Head of Sales at each company in accounts.csv and save them to a lead list.",
          command:
            'wonda linkedin salesnav search "head of sales" --company "Northwind"',
        },
        {
          platform: "linkedin",
          glyph: "inbox",
          ask: "Who’s waiting on a reply in my LinkedIn messages?",
          command: "wonda linkedin conversations",
        },
        {
          platform: "x",
          glyph: "search",
          ask: "Find people on X asking how to automate outbound this week.",
          command: 'wonda x search "automate outbound" --time week -n 50',
        },
        {
          platform: "reddit",
          glyph: "reply",
          ask: "Find threads in r/SaaS asking for a CRM and tell me which to answer.",
          command: "wonda reddit feed SaaS --sort new",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("Claude Code"),
      {
        question: "Does Claude Code send anything without asking me?",
        answer:
          "Only if you let it. Claude Code asks before it runs a command unless you’ve allowed it, and Sales Navigator messages and invites are shown to you as a preview before they send.",
      },
      {
        question: "Can it work from a spreadsheet?",
        answer:
          "Yes. Point Claude Code at a CSV or a notes file. It reads the rows, runs a search or an action for each one and writes the results back.",
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
      FAQ_SALES_NAV,
    ],
  },

  "codex-for-social-media-marketing": {
    kind: "agent",
    label: "Codex",
    agent: "Codex",
    heading: ["Codex", "for social media marketing"],
    sub: [
      "Ask Codex about your market, not just your code.",
      "It reads what people say on LinkedIn, X and Reddit and turns it into reports, lead lists and replies, signed in as you.",
    ],
    metaTitle: "Codex for Social Media Marketing | Wonda",
    tasks: {
      title: "From the timeline to your repo.",
      items: [
        {
          platform: "x",
          glyph: "filter",
          ask: "Sort this week’s posts about our product into bugs, requests and praise.",
          command: 'wonda x search "acme" --sort latest --time week -n 100',
        },
        {
          platform: "reddit",
          glyph: "search",
          ask: "Collect the Reddit threads comparing us with Northwind.",
          command:
            'wonda reddit search "acme vs northwind" --sort top --time month',
        },
        {
          platform: "linkedin",
          glyph: "heart",
          ask: "Who reacted to our launch post? List the ones at companies we sell to.",
          command:
            "wonda linkedin enrich-engagers --activity-id 7391204475318231040",
        },
        {
          platform: "sales-navigator",
          glyph: "eye",
          ask: "Write a connection note for each lead in my Q4 list and show me before sending.",
          command:
            'wonda linkedin salesnav connect "urn:li:fs_salesProfile:(…)" --expect-name "Ada Park" --note "…"',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("Codex"),
      {
        question: "Do I need the MCP server for the Codex app?",
        answer: `No. The Codex app and the Codex CLI both run wonda commands on your computer. Add the MCP server (Plugins, then MCPs, URL ${MCP_URL}) only if Codex should drive Wonda on a different computer.`,
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
      FAQ_SALES_NAV,
    ],
  },

  "cursor-for-social-media": {
    kind: "agent",
    label: "Cursor",
    agent: "Cursor",
    heading: ["Cursor", "for social media"],
    sub: [
      "Cursor’s agent already knows your product from its code.",
      "Add Wonda and it finds what people say about it on LinkedIn, Sales Navigator, X and Reddit, and answers them from your accounts.",
    ],
    metaTitle: "Cursor for Social Media | Wonda",
    tasks: {
      title: "Support and outreach, next to the code.",
      items: [
        {
          platform: "reddit",
          glyph: "reply",
          ask: "Reply to the Reddit threads about the export bug with the fix.",
          command: 'wonda reddit comment t3_1g7h2kd --text "…"',
        },
        {
          platform: "x",
          glyph: "search",
          ask: "Find developers on X asking how to do what our API does.",
          command:
            'wonda x search "parse invoices api" --sort latest --time week',
        },
        {
          platform: "linkedin",
          glyph: "inbox",
          ask: "Check my LinkedIn messages for anyone asking about the API.",
          command: "wonda linkedin conversations",
        },
        {
          platform: "sales-navigator",
          glyph: "list",
          ask: "Find the engineering managers at our trial accounts and save them as leads.",
          command:
            'wonda linkedin salesnav search "engineering manager" --company "Northwind"',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("Cursor’s agent"),
      {
        question: "Do I need anything besides Cursor?",
        answer:
          "Only Wonda. Cursor’s agent runs the wonda CLI in its terminal, so there’s no extension or connector to add.",
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
      FAQ_SALES_NAV,
    ],
  },

  "openclaw-for-social-media": {
    kind: "agent",
    label: "OpenClaw",
    agent: "OpenClaw",
    heading: ["OpenClaw", "for social media"],
    sub: [
      "OpenClaw answers you on WhatsApp or Telegram.",
      "With Wonda it checks LinkedIn, Sales Navigator, X and Reddit on a schedule and tells you what’s worth acting on.",
    ],
    metaTitle: "OpenClaw for Social Media | Wonda",
    tasks: {
      title: "Checks that come to you.",
      items: [
        {
          platform: "linkedin",
          glyph: "clock",
          ask: "Each morning, tell me who reacted to yesterday’s post and which of them are founders.",
          command: "wonda linkedin reactions 7391204475318231040",
        },
        {
          platform: "sales-navigator",
          glyph: "bell",
          ask: "Check my Sales Navigator alerts and tell me what’s worth acting on.",
          command: "wonda linkedin salesnav alerts",
        },
        {
          platform: "x",
          glyph: "feed",
          ask: "Every morning, scroll my feed for five minutes and like posts from my top customers.",
          command:
            'wonda x feed-engage --authors "ada,grace,linus" --duration 5m',
        },
        {
          platform: "reddit",
          glyph: "eye",
          ask: "Watch r/selfhosted for people asking about backups and message me.",
          command: "wonda reddit feed selfhosted --sort new",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("OpenClaw"),
      {
        question: "Can OpenClaw run these on a schedule?",
        answer: `Yes. OpenClaw can repeat a check every morning or every hour and message you the result. Each run is one pass. ${LIMITS}`,
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
      FAQ_SALES_NAV,
    ],
  },

  "hermes-for-social-media": {
    kind: "agent",
    label: "Hermes",
    agent: "Hermes",
    heading: ["Hermes", "for social media"],
    sub: [
      "Hermes remembers the routines you teach it.",
      "With Wonda it runs your weekly LinkedIn, Sales Navigator, X and Reddit checks, signed in as you.",
    ],
    metaTitle: "Hermes Agent for Social Media | Wonda",
    tasks: {
      title: "Your weekly routine, run for you.",
      items: [
        {
          platform: "linkedin",
          glyph: "inbox",
          ask: "Every Friday, list the LinkedIn messages I haven’t answered.",
          command: "wonda linkedin conversations",
        },
        {
          platform: "sales-navigator",
          glyph: "search",
          ask: "Every week, find new VPs of Sales at the companies on my target list.",
          command:
            'wonda linkedin salesnav search "VP sales" --seniority "Vice President" --company "Northwind"',
        },
        {
          platform: "x",
          glyph: "reply",
          ask: "Reply when someone on X asks for an alternative to Northwind.",
          command:
            'wonda x feed-engage --keywords "northwind alternative" --reply --max-reply 3',
        },
        {
          platform: "reddit",
          glyph: "clock",
          ask: "Every Monday, find r/startups threads asking for a tool like ours.",
          command: "wonda reddit feed startups --sort new",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("Hermes"),
      {
        question: "What does the Wonda skill add to Hermes?",
        answer:
          "It teaches Hermes every Wonda command: what each one does, which flags to use and how LinkedIn’s daily limits work.",
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
      FAQ_SALES_NAV,
    ],
  },

  "chatgpt-for-social-media": {
    kind: "agent",
    label: "ChatGPT",
    agent: "ChatGPT",
    heading: ["ChatGPT", "for social media"],
    sub: [
      "Ask ChatGPT to work LinkedIn, Sales Navigator, X and Reddit for you.",
      "It searches, reads your inboxes and reaches out from your own accounts, once you approve.",
    ],
    metaTitle: "ChatGPT for Social Media | Wonda",
    tasks: {
      title: "Ask in chat, approve, done.",
      items: [
        {
          platform: "linkedin",
          glyph: "bell",
          ask: "Sum up my LinkedIn notifications from this week.",
          command: "Wonda · linkedin_notifications",
        },
        {
          platform: "sales-navigator",
          glyph: "list",
          ask: "Save the five I liked to a lead list called Madrid retail.",
          command: "Wonda · linkedin_salesnav_list_add",
        },
        {
          platform: "x",
          glyph: "mention",
          ask: "Reply to my new mentions on X.",
          command: "Wonda · x_mentions",
        },
        {
          platform: "reddit",
          glyph: "message",
          ask: "What do people on r/marketing think about AI SDRs this month?",
          command: "Wonda · reddit_search",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("ChatGPT"),
      {
        question: "Which ChatGPT plans can connect Wonda?",
        answer:
          "Adding Wonda needs ChatGPT’s Developer mode, which is available on Business, Enterprise and Edu plans. On Plus or Pro, use Codex, which comes with your ChatGPT plan.",
      },
      FAQ_CONNECTOR_KEYS("ChatGPT"),
      {
        question: "Do I approve every action?",
        answer:
          "Actions that post, comment or message ask for your approval by default. Whatever you allow in ChatGPT, LinkedIn actions still count against daily limits; Wonda warns when you pass one, or stops if you choose.",
      },
      FAQ_SAFE,
    ],
  },

  "claude-for-social-media": {
    kind: "agent",
    label: "Claude",
    agent: "Claude",
    heading: ["Claude", "for social media"],
    sub: [
      "Ask Claude, on desktop or in Cowork, to work your social accounts.",
      "It searches LinkedIn, Sales Navigator, X and Reddit, goes through your inboxes and reaches out as you.",
    ],
    metaTitle: "Claude for Social Media, in Desktop and Cowork | Wonda",
    tasks: {
      title: "Longer jobs, with your files.",
      items: [
        {
          platform: "linkedin",
          glyph: "connect",
          ask: "Find everyone on this attendee list on LinkedIn and connect with a short note.",
          command: "Wonda · linkedin_connect",
        },
        {
          platform: "sales-navigator",
          glyph: "list",
          ask: "Find heads of marketing at fintechs in Berlin and save them to a list.",
          command: "Wonda · linkedin_salesnav_search",
        },
        {
          platform: "x",
          glyph: "message",
          ask: "What are people saying about our launch on X today?",
          command: "Wonda · x_search",
        },
        {
          platform: "reddit",
          glyph: "reply",
          ask: "Answer the new comments on my r/startups post.",
          command: "Wonda · reddit_post",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("Claude"),
      {
        question: "Does it work in Cowork?",
        answer:
          "Yes. Cowork uses the same connector, so Claude can take on longer jobs there, like working through a list of people to find and contact.",
      },
      {
        question: "Is there a local option?",
        answer:
          "Yes. Claude desktop can load the Wonda desktop extension instead, which runs everything on your computer through your local Wonda browser.",
      },
      FAQ_CONNECTOR_KEYS("Claude"),
      {
        question: "Can my team use it?",
        answer:
          "Yes. On Claude Team and Enterprise, an admin adds Wonda once as a custom connector, then members approve it for themselves.",
      },
      FAQ_SAFE,
    ],
  },

  "search-profiles": {
    kind: "use-case",
    label: "Search profiles",
    heading: ["Search profiles", "across LinkedIn, X and Reddit"],
    sub: [
      "Your agent finds the candidates, leads, competitors or influencers that fit what you want.",
      "In the background, safely and securely.",
    ],
    metaTitle:
      "Search LinkedIn, Sales Navigator, X and Reddit Profiles | Wonda",
    description:
      "Find candidates, competitors, influencers and leads with your AI agent. It searches LinkedIn, Sales Navigator, X and Reddit from your own accounts and reads the profiles that fit.",
    tasks: {
      title: "Whoever you’re looking for.",
      items: [
        {
          platform: "sales-navigator",
          glyph: "filter",
          ask: "Find VPs of Sales at software companies in Germany and save them to a lead list.",
          command:
            'wonda linkedin salesnav search "VP sales" --seniority "Vice President" --region "Germany"',
        },
        {
          platform: "linkedin",
          glyph: "search",
          ask: "Find senior product designers in London and read their profiles.",
          command:
            'wonda linkedin search "senior product designer London" --type PEOPLE',
        },
        {
          platform: "x",
          glyph: "users",
          ask: "Find the accounts on X that post the most about AI agents, with their follower counts.",
          command: 'wonda x search "AI agents" --sort top --time month',
        },
        {
          platform: "reddit",
          glyph: "profile",
          ask: "Find the Reddit users who keep recommending Northwind and read what else they post.",
          command: 'wonda reddit search "Northwind" --sort top --time month',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      {
        question: "Can it search candidates on LinkedIn Recruiter?",
        answer:
          "Yes, if your LinkedIn account has a Recruiter Lite seat: candidate search with Recruiter filters, full profiles and your saved searches. Without one, regular LinkedIn people search works with any account.",
      },
      {
        question: "Can it export what it finds?",
        answer:
          "Yes. Sales Navigator searches export to CSV, and your agent can put any results in a lead list, a spreadsheet or a file.",
      },
      FAQ_SALES_NAV,
      FAQ_SAFE,
    ],
  },
  "send-messages": {
    kind: "use-case",
    label: "Send messages",
    heading: ["Send messages", "from your own accounts"],
    sub: [
      "Your agent messages your leads, candidates and creators in the background, exactly like you’d write to them yourself.",
    ],
    metaTitle: "AI Outreach and Messaging on LinkedIn, X and Reddit | Wonda",
    description:
      "Sales outbound, candidate follow-ups and influencer coordination from your own LinkedIn, Sales Navigator, X and Reddit accounts. Your agent writes and sends at a human pace; you approve.",
    tasks: {
      title: "Every conversation, handled.",
      items: [
        {
          platform: "sales-navigator",
          glyph: "eye",
          ask: "Write an InMail to each lead in my Q4 list and show me every one before it sends.",
          command:
            'wonda linkedin salesnav message <lead> --text "Hi Ada, ..." --expect-name "Ada Lovelace"',
        },
        {
          platform: "linkedin",
          glyph: "inbox",
          ask: "Follow up with the candidates who haven’t replied to me in a week.",
          command: 'wonda linkedin send-message <conversation-urn> "Hi Sam, …"',
        },
        {
          platform: "x",
          glyph: "message",
          ask: "DM the creators we’re working with on X about next week’s launch.",
          command: 'wonda x dm start @samlee --text "…"',
        },
        {
          platform: "reddit",
          glyph: "reply",
          ask: "Start a chat with everyone who asked for a demo in our r/SaaS thread.",
          command: 'wonda reddit chat start samlee --text "…"',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      {
        question: "Does it send without asking me?",
        answer:
          "Only when you tell it to. On Sales Navigator every message and invite is shown to you first. Elsewhere, ask your agent for drafts; LinkedIn InMail and X DMs also have a dry run that types the message without sending it.",
      },
      {
        question: "Are there daily limits?",
        answer:
          "Yes. Every message counts against daily limits. On LinkedIn the default is 20 invites and messages a day; Wonda warns when you pass one, or stops if you choose.",
      },
      {
        question: "Will it spend my InMail credits?",
        answer:
          "Not without asking. If an InMail would use a credit, Wonda stops and asks first.",
      },
      FAQ_SAFE,
    ],
  },
  "analyze-posts-and-comments": {
    kind: "use-case",
    label: "Analyze posts & comments",
    heading: ["Analyze posts", "and comments"],
    sub: [
      "Your agent reads the posts and comments that matter from your feed or any search.",
      "Use for inspiration or hyper target your leads.",
    ],
    metaTitle: "Analyze LinkedIn, X and Reddit Posts and Comments | Wonda",
    description:
      "Your AI agent reads your feed, searches and comment threads on LinkedIn, X and Reddit, and sums up who said what, what worked and what to answer.",
    tasks: {
      title: "What people are saying.",
      items: [
        {
          platform: "linkedin",
          glyph: "heart",
          ask: "Sum up the comments on our CEO’s last post: who’s positive and who has questions.",
          command: "wonda linkedin comments 7391204475318231040 --via wab",
        },
        {
          platform: "x",
          glyph: "feed",
          ask: "Go through my X timeline from today and pull out the posts about our space.",
          command: "wonda x home",
        },
        {
          platform: "reddit",
          glyph: "eye",
          ask: "Read this week’s top r/marketing threads and list the questions people keep asking.",
          command: "wonda reddit feed marketing --sort top",
        },
        {
          platform: "sales-navigator",
          glyph: "bell",
          ask: "Go through my Sales Navigator alerts and tell me which leads are worth a message today.",
          command: "wonda linkedin salesnav alerts",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      {
        question: "Can it read the comments under any post?",
        answer:
          "Yes. On LinkedIn it reads the comments and reactions on a post, with each person’s role and company; on X, replies and threads; on Reddit, whole comment threads.",
      },
      {
        question: "Does it read my own feed?",
        answer:
          "On X and Reddit it reads your timeline and the subreddits you pick, signed in as you. On LinkedIn it searches posts by keyword and reads the posts and comments you point it to.",
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
    ],
  },
  "ai-agents-for-marketing": {
    kind: "use-case",
    label: "AI agents for marketing",
    heading: ["AI agents", "for marketing"],
    sub: [
      "Hand your agent the social side of your marketing.",
      "It finds the conversations about your space on LinkedIn, X and Reddit, joins them, posts and follows up from your own accounts.",
    ],
    metaTitle: "AI Agents for Marketing on LinkedIn, X and Reddit | Wonda",
    description:
      "AI marketing automation that works the way you do: your agent finds the conversations on LinkedIn, X and Reddit, joins them, posts and follows up from your own accounts.",
    tasks: {
      title: "Your marketing, every day.",
      items: [
        {
          platform: "linkedin",
          glyph: "post",
          ask: "Post our launch note, then like and answer the first comments.",
          command: 'wonda linkedin post "We just shipped…"',
        },
        {
          platform: "x",
          glyph: "reply",
          ask: "Find people on X asking for a tool like ours this week and reply where we can help.",
          command: 'wonda x search "alternative to" --sort top --time week',
        },
        {
          platform: "reddit",
          glyph: "search",
          ask: "Find r/marketing threads about our category and draft comments for me to check first.",
          command: 'wonda reddit comment t3_1gk2x9a --text "…" --dry-run',
        },
        {
          platform: "sales-navigator",
          glyph: "list",
          ask: "Find heads of marketing at our target accounts and save them to a lead list.",
          command:
            'wonda linkedin salesnav search "head of marketing" --company "Northwind"',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      FAQ_AGENTS,
      {
        question: "Will it post without asking me?",
        answer:
          "It does what you ask. Ask your agent to show you each post or comment first; on Reddit, a dry run types the comment without posting it so you can check it.",
      },
      {
        question: "Does it keep to the platforms’ limits?",
        answer:
          "Yes. Every action counts against daily limits. On LinkedIn the defaults are 3 posts and 10 comments a day; Wonda warns when you pass one, or stops if you choose.",
      },
      FAQ_PLATFORMS,
      FAQ_SAFE,
    ],
  },
  "multi-account-social-media": {
    kind: "use-case",
    label: "Multi-account",
    heading: ["Multi-account", "social media management"],
    sub: [
      "Run every LinkedIn, X and Reddit account you manage from one agent.",
      "Each account stays separate, with its own sign-in and its own daily limits.",
    ],
    metaTitle: "Multi-Account Social Media Management | Wonda",
    tasks: {
      title: "One agent, every account.",
      items: [
        {
          platform: "linkedin",
          glyph: "users",
          ask: "Send this week’s invites from each founder’s profile, 15 each.",
          command: 'wonda linkedin connect ada-park -m "…" --account founder-2',
        },
        {
          platform: "sales-navigator",
          glyph: "search",
          ask: "Run the same lead search on both of our Sales Navigator seats.",
          command:
            'wonda linkedin salesnav search "VP sales" --region "Germany" --account seat-2',
        },
        {
          platform: "x",
          glyph: "mention",
          ask: "Show me the new mentions across all our X accounts.",
          command: "wonda inbound --platform x",
        },
        {
          platform: "reddit",
          glyph: "inbox",
          ask: "Answer the support questions in the brand account’s Reddit inbox.",
          command: "wonda reddit inbox --unread --account acme-support",
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      FAQ_AGENTS,
      {
        question: "Are the accounts kept apart?",
        answer:
          "Yes. Each account lives in its own Wonda browser profile with its own sign-in, and has its own daily action limits.",
      },
      {
        question: "Can one browser profile hold several accounts?",
        answer:
          "Yes. If one person runs a LinkedIn, an X and a Reddit account, bind them to one profile with wonda wab bind.",
      },
      {
        question: "Can I see what each account did today?",
        answer:
          "Yes. wonda actions lists each account’s actions over the last 24 hours against its daily limits.",
      },
      FAQ_SAFE,
    ],
  },

  "competitor-analysis-tool": {
    kind: "use-case",
    label: "Competitor analysis",
    heading: ["Competitor analysis", "for social media"],
    sub: [
      "Ask your agent what your competitors posted, what worked and who engaged.",
      "It reads LinkedIn, Sales Navigator, X and Reddit, signed in as you, and brings back the answer.",
    ],
    metaTitle: "Competitor Analysis Tool for LinkedIn, X and Reddit | Wonda",
    tasks: {
      title: "Ask about any competitor.",
      items: [
        {
          platform: "linkedin",
          glyph: "heart",
          ask: "Who engaged with Northwind’s launch post? Show me the ones who match our buyers.",
          command:
            "wonda linkedin enrich-engagers --activity-id 7390012345678901234",
        },
        {
          platform: "sales-navigator",
          glyph: "profile",
          ask: "Who joined Northwind’s sales team in the last 90 days?",
          command:
            'wonda linkedin salesnav search --company "Northwind" --function Sales',
        },
        {
          platform: "x",
          glyph: "chart",
          ask: "Which of Northwind’s posts got the most views this quarter?",
          command: "wonda x user-tweets @northwindhq -n 100",
        },
        {
          platform: "reddit",
          glyph: "message",
          ask: "What are people on Reddit saying about Northwind?",
          command: 'wonda reddit search "northwind" --sort top --time month',
        },
      ],
    },
    faq: [
      FAQ_WHAT_IS("your agent"),
      FAQ_AGENTS,
      {
        question: "What can it see?",
        answer:
          "What you can see when you’re signed in: posts, profiles, company pages, reactions and comments. Nothing that’s private to your competitor.",
      },
      {
        question: "Can it look at competitors’ ads?",
        answer:
          "Yes. wonda scrape ads searches the Meta Ad Library for a keyword, such as a competitor’s name, so your agent can see the ads they’re running.",
      },
      {
        question: "Can it check on competitors every week?",
        answer: `Yes. Ask your agent to rerun the same check on a schedule and send you what changed. ${LIMITS}`,
      },
      FAQ_SAFE,
    ],
  },
};
